/**
 * Custom DateAdapter that ensures dates stay in local timezone
 * Prevents "one day back" bug when sending dates to API
 */
import { Injectable } from "@angular/core"
import { NativeDateAdapter } from "@angular/material/core"

@Injectable()
export class LocalDateAdapter extends NativeDateAdapter {
  /**
   * Override format to use locale-friendly display (DD.MM.YYYY for de-DE)
   * while keeping local timezone to prevent the "one day back" bug.
   *
   * `MatDatepickerInput` (the `matDatepicker` directive on a plain
   * `matInput`) never actually passes the literal string `"input"` here —
   * with `MatNativeDateModule` it passes `MAT_NATIVE_DATE_FORMATS.display
   * .dateInput`, i.e. the object `{ year: "numeric", month: "numeric",
   * day: "numeric" }`. Matching only on `=== "input"` (as before) made this
   * branch dead code at runtime: the text field silently fell back to
   * `super.format()`, which is fine for de-DE ordering but drops the zero
   * padding (e.g. "5.3.2026" instead of "05.03.2026"). Detect that
   * numeric-date shape too so the padded DD.MM.YYYY format is actually
   * applied to the input field.
   */
  override format(date: Date, displayFormat: Object): string {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")

    if (displayFormat === "input" || this.isNumericDateInputFormat(displayFormat)) {
      // Locale format for input fields: DD.MM.YYYY
      return `${day}.${month}.${year}`
    }

    // For display, use parent's logic (respects locale)
    return super.format(date, displayFormat)
  }

  private isNumericDateInputFormat(displayFormat: Object): boolean {
    if (typeof displayFormat !== "object" || displayFormat === null) {
      return false
    }
    const format = displayFormat as Record<string, unknown>
    return (
      format["year"] === "numeric" && format["month"] === "numeric" && format["day"] === "numeric"
    )
  }

  /**
   * Parse date string to Date object (local timezone).
   * Accepts DD.MM.YYYY / DD/MM/YYYY (locale) and YYYY-MM-DD (ISO) formats.
   *
   * IMPORTANT: for any other string shape we deliberately return `null`
   * instead of falling through to the native `Date.parse()` (via
   * `super.parse()`). The native parser treats slash- or dot-separated
   * dates as the US MM/DD/YYYY convention, which silently swaps day and
   * month for our DE locale (e.g. typing "10.04.2026" for 10 April, or
   * pasting a "10/04/2026" value, would be read back as 4 October). See
   * local-date-adapter.spec.ts for a reproduction.
   */
  override parse(value: any): Date | null {
    if (typeof value !== "string") {
      return super.parse(value)
    }

    // DD.MM.YYYY or DD/MM/YYYY (German locale format, day first)
    const localMatch = value.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/)
    if (localMatch) {
      const [day, month, year] = localMatch.slice(1).map(Number)
      if (month < 1 || month > 12 || day < 1 || day > 31) {
        return null
      }
      return new Date(year, month - 1, day)
    }

    // YYYY-MM-DD (ISO format — still accepted for API values / pasted data)
    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (isoMatch) {
      const [year, month, day] = isoMatch.slice(1).map(Number)
      return new Date(year, month - 1, day)
    }

    return null
  }
}

/**
 * Utility function to format Date for API (YYYY-MM-DD in local timezone)
 * Use this when manually converting dates outside of mat-datepicker
 */
export function formatDateForAPI(date: Date | null | undefined): string | undefined {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return undefined
  }

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}
